import { IconButton } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { MdMessage, MdStar } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { useMatching } from '../../../../../hooks/useMatching';
import useMessaging from '../../../../../hooks/useMessaging';
import { MessagingAttendee, UserMatch } from '../../../../../interfaces/interfaces';
import { setOpenAttendeeMessaging } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { VyLoader } from '../../../../common/VyLoader';
import VyTooltip from '../../../../common/VyTooltip';
import AttendeeAvatar from '../AttendeeAvatar';
import { ScoreIndicator } from '../UIComponents/SpeedDatingUiComponent/ScoreIndicator';

export interface MatchListProps {
  disableScore: boolean;
  hideScoreWhenZero?: boolean;
  list: UserMatch[] | undefined;
  updateList: () => void;
  currentMatch?: UserMatch;
}

export const MatchList = (props: MatchListProps) => {
  const { disableScore, list, updateList, hideScoreWhenZero = false, currentMatch } = props;

  // Hooks
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const matching = useMatching();
  const { openMessagingConversationFromAttendee } = useMessaging();

  // Selectors
  const event = useSelector(({ common }) => common.event);
  const registration = useSelector(({ common }) => common.eventRegistration);

  // States
  const [loading, setLoading] = useState(false);

  // Functions
  function getUserMatchPartner(match: UserMatch) {
    if (match.participantA.registration._id === registration?._id) return { _id: match._id, own: match.participantA, partner: match.participantB };
    else return { _id: match._id, own: match.participantB, partner: match.participantA };
  }

  function getSortedList() {
    return list?.slice()?.sort((a, b) => {
      if (!b.datetime_last_match || !a.datetime_last_match) return -1;
      return new Date(b.datetime_last_match.toString()).getTime() - new Date(a.datetime_last_match.toString()).getTime();
    });
  }

  async function highlight(match: UserMatch) {
    setLoading(true);
    try {
      await matching.toggleHighlight(match);
      await updateList();
    } catch (err) {
      console.log('Error while updating highlight', err);
    } finally {
      setLoading(false);
    }
  }
  const dateAndScoreElement = (match: ReturnType<typeof getUserMatchPartner>, i: UserMatch, isCurrentMatch: boolean) => (
    <>
      <VyTooltip title={t('matching:all-matches.time-of-meeting')}>
        <div className="flex justify-start @sm:justify-center ml-11 @sm:ml-0">
          {isCurrentMatch ? t('matching:history.currently-running') : i.datetime_last_match && format(new Date(i.datetime_last_match), 'dd.MM.yy HH:mm')}
        </div>
      </VyTooltip>
      <div className="w-1/4 flex justify-center items-center">
        <ScoreIndicator score={i.score} disabled={disableScore || (i.score === 0 && hideScoreWhenZero)} />
      </div>
    </>
  );

  // Returned component
  return (
    <div className="my-5">
      {list && list.length > 0 ? (
        <div className={`relative`}>
          {loading && (
            <div className={`absolute w-full h-full`}>
              <VyLoader onlyPartial />
            </div>
          )}
          {getSortedList()?.map((i) => {
            const match = getUserMatchPartner(i);
            const isCurrentMatch = match._id === currentMatch?._id;

            return (
              <div>
                <div key={match._id} className="w-full flex justify-between items-center">
                  <div className="flex flex-row items-center space-x-1 w-full @sm:w-1/4">
                    <div className={isCurrentMatch ? 'animate-pulse' : ''}>
                      <AttendeeAvatar attendee={match.partner.registration.user as MessagingAttendee} />
                    </div>
                    <span>{match.partner.registration.user?.name}</span>
                  </div>
                  <div className="hidden @sm:block">{dateAndScoreElement(match, i, isCurrentMatch)}</div>
                  <div className="flex w-1/4 justify-end">
                    {event?.settings?.allowAttendeeMessaging && (
                      <VyTooltip title={t('meeting:speeddating.history.start-chat', { name: match.partner.registration.user?.name })}>
                        <IconButton
                          onClick={() => {
                            dispatch(setOpenAttendeeMessaging(true));
                            openMessagingConversationFromAttendee(match.partner.registration.user);
                          }}
                          size="large">
                          <MdMessage />
                        </IconButton>
                      </VyTooltip>
                    )}
                    <VyTooltip title={t('meeting:speeddating.history.highlight')}>
                      <IconButton
                        onClick={async () => {
                          await matching.toggleHighlight(i);
                          await updateList();
                        }}
                        size="large">
                        <MdStar color={match.own.hasHighlightedMatch ? event?.template.mainColor : ''} />
                      </IconButton>
                    </VyTooltip>
                  </div>
                </div>
                <div className="w-full @sm:hidden">{dateAndScoreElement(match, i, isCurrentMatch)}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="w-full flex justify-center">{t('matching:no-list-entries')}</div>
      )}
    </div>
  );
};
